import { subMonths, subYears } from 'date-fns'
import type {
  PeriodQuickSelect,
  PeriodValue,
  PipelineStageDatum,
} from '@firstbite/ui'
import type { SalesStage } from 'models/sales_stages'
import { formatCurrency } from '../../utils/formatting'
import {
  DEFAULT_STAGE_COLOR,
  getStageColorSolid,
} from '../SalesStage/stageColors'
import type { HomepageStats } from '../../services/recommendations_api'

/**
 * Pure data helpers for the fb-ui Performance dashboard, kept out of the
 * component so the window math + pipeline mapping are unit-testable.
 */

/**
 * Our preset windows (mirrors the legacy RangeSelector), as calendar ranges.
 * Each `start` is computed relative to a `now` passed in at call time — never a
 * mount-frozen date — so a re-applied preset always covers up to the moment of
 * the click.
 */
export const PRESETS: { label: string; start: (now: Date) => Date }[] = [
  { label: 'Last 3 months', start: (now) => subMonths(now, 3) },
  { label: 'Last 6 months', start: (now) => subMonths(now, 6) },
  { label: 'Last 12 months', start: (now) => subMonths(now, 12) },
  { label: 'Year to date', start: (now) => new Date(now.getFullYear(), 0, 1) },
  // "All time" — mirror the backend's 5-year floor for its "all" preset. Because
  // we always send this as a `custom` range (explicit start/end), the backend
  // returns the window verbatim and does NOT apply its own cap, so we replicate
  // it here — otherwise a floor like Jan-2000 yields ~300 monthly periods.
  // Keep in sync with `get_date_range` "all" in
  // server/recommendations/views/performance_stats_views.py (relativedelta(years=5)).
  { label: 'All time', start: (now) => subYears(now, 5) },
]

/** The default applied window — "Last 6 months". */
export const DEFAULT_PRESET_LABEL = 'Last 6 months'

/** A preset → the picker's `PeriodValue` for the given `now`. */
function periodForPreset(
  preset: { label: string; start: (now: Date) => Date },
  now: Date
): PeriodValue {
  return {
    mode: 'custom',
    start: preset.start(now),
    end: now,
    label: preset.label,
  }
}

/** Build the picker quick-selects for `now` (windows are relative to it). */
export function buildQuickSelects(now: Date): PeriodQuickSelect[] {
  return PRESETS.map((p) => ({
    label: p.label,
    value: periodForPreset(p, now),
  }))
}

/** The default applied period ("Last 6 months") for `now`. */
export function defaultPeriodFor(now: Date): PeriodValue {
  const preset =
    PRESETS.find((p) => p.label === DEFAULT_PRESET_LABEL) ?? PRESETS[0]
  return periodForPreset(preset, now)
}

/**
 * Resolve the window to actually apply. Quick-select windows are built once (at
 * mount) for the picker UI, so a preset re-applied later — e.g. a dashboard tab
 * left open across a day boundary — would otherwise send a stale mount-time
 * window and silently exclude newer data. When the applied value matches a known
 * preset (by label), recompute its window fresh from `now`; a hand-picked
 * calendar range (no preset-label match) is applied verbatim.
 *
 * The label is the source of truth for "this is preset X" — so this trusts it
 * over the incoming dates. That's safe because `PeriodPicker` clears the preset
 * label the moment the user edits the calendar (see its `RangeCalendar.onChange`),
 * so a hand-tuned range never reaches here still carrying a preset's label.
 */
export function resolveAppliedPeriod(
  value: PeriodValue,
  now: Date
): PeriodValue {
  const preset = PRESETS.find((p) => p.label === value.label)
  return preset ? periodForPreset(preset, now) : value
}

/**
 * Map homepage pipeline stats → the fb-ui `PipelineBar` data: count-based
 * segment widths, the user-assigned deal-stage color (`colorValue`), and the
 * per-stage `$` value in the segment tooltip (`valueLabel`). Stages with no open
 * deals are dropped; an unknown stage falls back to the neutral default color.
 */
export function toPipelineData(
  pipelineStats: HomepageStats | null | undefined,
  allStages: SalesStage[] | undefined,
  customOrderEnabled: boolean
): PipelineStageDatum[] {
  if (!pipelineStats) return []
  return pipelineStats.deals_by_stage
    .filter((s) => s.count > 0)
    .map((s) => {
      const full = allStages?.find((st) => st.id === s.stage_id)
      return {
        label: s.stage_name,
        // `color` is a required BadgeColor placeholder; `colorValue` (the app's
        // actual user-assigned deal-stage color) overrides it in the widget.
        color: 'gray' as const,
        value: s.count,
        // Per-stage $ value, shown after the count in the segment tooltip.
        valueLabel: formatCurrency(s.total_value),
        colorValue:
          full && allStages
            ? getStageColorSolid(full, allStages, customOrderEnabled)
            : DEFAULT_STAGE_COLOR,
      }
    })
}
